import { OTP_LENGTH } from "$lib/constants/two-factor"
import type { FormDataAdapter } from "$lib/util"
import {
  Address,
  SUFFIX_NAMES,
  ValidateYup,
  isInListCaseInsensitive,
  type Customer,
} from "@tastyworks/tastyworks-api"
import { isEqual } from "lodash-es"
import { boolean, mixed, object, string, type InferType } from "yup"
import { addressAdapter, addressSchema } from "./../address"
import {
  phone,
  phoneNumberAdapter,
  type PhoneNumberConfig,
} from "./../util/phone-number"

export const contactNameSchema = ValidateYup.customerSchema
  .pick(["firstName", "lastName", "middleName"])
  .concat(
    object({
      suffixName: string()
        .test("valid-suffix", "yup.common.string.suffix", (value) =>
          isInListCaseInsensitive(SUFFIX_NAMES, value)
        )
        .default(""),
    })
  )

export const contactPhoneNumberSchema = object({
  shouldValidatePhone: boolean().default(true),
  mobilePhoneNumber: mixed<PhoneNumberConfig>().when("shouldValidatePhone", {
    is: true,
    then: () => phone().required(),
  }),
})

export const contactSmsVerificationSchema = object({
  otp: string().required().length(OTP_LENGTH),
})

export const contactPhysicalAddressSchema = object({
  physicalAddress: addressSchema,
  isMailingAddressSameAsHome: boolean().required(),
})

export const contactMailingAddressSchema = object({
  mailingAddress: addressSchema.concat(
    object({
      enableExplanation: boolean().default(false),
      letterOfExplanation: string().when("enableExplanation", {
        is: true,
        then: (schema) => schema.required(),
      }),
    })
  ),
})

export const contactInfoSchema = object({
  name: contactNameSchema,
  phoneNumber: contactPhoneNumberSchema,
  smsVerification: contactSmsVerificationSchema,
  physicalAddress: contactPhysicalAddressSchema,
  mailingAddress: contactMailingAddressSchema,
})

export const contactNameAdapter: FormDataAdapter<
  Customer,
  InferType<typeof contactNameSchema>
> = {
  toForm: (customer, contactInfoName) => {
    contactInfoName.firstName = customer.firstName
    contactInfoName.middleName = customer.middleName
    contactInfoName.lastName = customer.lastName
    contactInfoName.suffixName = customer.suffixName

    return contactInfoName
  },
  toModel: (contactInfoName, customer) => {
    customer.firstName = contactInfoName.firstName
    customer.middleName = contactInfoName.middleName ?? ""
    customer.lastName = contactInfoName.lastName
    customer.suffixName = contactInfoName.suffixName ?? ""

    return customer
  },
}

export const contactPhoneNumberAdapter: FormDataAdapter<
  Customer,
  InferType<typeof contactPhoneNumberSchema>
> = {
  toForm: (customer, contactInfoPhoneNumber) => {
    contactInfoPhoneNumber.shouldValidatePhone =
      !customer.hasPendingOrApprovedApplication // Phone form field is disabled if existing applications, so should not block
    contactInfoPhoneNumber.mobilePhoneNumber = phoneNumberAdapter.toForm(
      customer.mobilePhoneNumber ?? ""
    )

    return contactInfoPhoneNumber
  },
  toModel: (contactInfoPhoneNumber, customer) => {
    customer.mobilePhoneNumber = phoneNumberAdapter.toModel(
      contactInfoPhoneNumber.mobilePhoneNumber
    )

    return customer
  },
}

export type ContactPhysicalAddressData = InferType<
  typeof contactPhysicalAddressSchema
>
export const contactPhysicalAddressAdapter: FormDataAdapter<
  Customer,
  ContactPhysicalAddressData
> = {
  toForm: (customer, contactInfoPhysicalAddress) => {
    contactInfoPhysicalAddress.physicalAddress = addressAdapter.toForm(
      customer.address,
      new Address()
    )
    contactInfoPhysicalAddress.isMailingAddressSameAsHome = isEqual(
      customer.address,
      customer.mailingAddress
    )

    return contactInfoPhysicalAddress
  },
  toModel: (contactInfoPhysicalAddress, customer) => {
    addressAdapter.toModel(
      contactInfoPhysicalAddress.physicalAddress,
      customer.address
    )

    return customer
  },
}

export const contactMailingAddressAdapter: FormDataAdapter<
  Customer,
  InferType<typeof contactMailingAddressSchema>
> = {
  toForm: (customer, contactInfoMailingAddress) => {
    contactInfoMailingAddress.mailingAddress = addressAdapter.toForm(
      customer.mailingAddress,
      new Address()
    )

    return contactInfoMailingAddress
  },
  toModel: (contactInfoMailingAddress, customer) => {
    addressAdapter.toModel(
      contactInfoMailingAddress.mailingAddress,
      customer.mailingAddress
    )

    return customer
  },
}
